import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { healthCyclesAPI } from '../../services/api'
import CycleWeekPanel from './CycleWeekPanel'
import { errorCode } from './cycleStatus'

// VH-229: weekly menus of a finalized cycle — one week at a time, current week first and open,
// each week shown as its plan (menu cards, weekend budgets), planned vs logged.
export function CycleWeeks({ cycleId, hasBaseline }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [error, setError] = useState(null)
  const [openPlanId, setOpenPlanId] = useState(null)

  const weeksQuery = useQuery({
    queryKey: ['healthCycleWeeks', cycleId],
    // The CACHE holds the unwrapped list, never the axios response. WeekPlanCard on the
    // Nutrition page reads this same key: when this component cached the response object
    // instead, that page crashed with "(s.data ?? []).find is not a function" (production,
    // 2026-09-17). Unwrapping in `select` only fixes the reader that declared it.
    queryFn: () => healthCyclesAPI.listWeeks(cycleId).then((r) => r.data ?? []),
    enabled: !!cycleId && hasBaseline,
  })

  const addWeek = useMutation({
    // An empty week: its plan comes from "same as last week" or a new plan, never from a composed fallback menu.
    mutationFn: () => healthCyclesAPI.addWeek(cycleId, { manual: true }),
    onSuccess: (response) => {
      setError(null)
      queryClient.setQueryData(['mealPrepPlan', response.data.id], response.data)
      queryClient.invalidateQueries({ queryKey: ['healthCycleWeeks', cycleId] })
      setOpenPlanId(response.data.id)
    },
    onError: (err) => setError(t(`cycleReports.errors.${errorCode(err)}`, t('healthCycle.errors.generic'))),
  })

  if (!hasBaseline) {
    return <p className="text-sm text-gray-600" data-testid="weeks-after-finalizing">{t('cycleReports.nutrition.weeksAfterFinalizing')}</p>
  }

  const weeks = weeksQuery.data ?? []
  const firstCurrent = weeks.find((w) => w.is_current && w.plan_id)?.plan_id
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-600">{t('cycleReports.nutrition.oneWeekAtATime')}</p>
        <button type="button" className="btn-primary text-sm" disabled={addWeek.isPending} onClick={() => addWeek.mutate()}>
          {addWeek.isPending ? t('cycleReports.nutrition.addingWeek') : t('cycleReports.nutrition.addWeek')}
        </button>
      </div>
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      {weeksQuery.isLoading && <div className="h-16 rounded-lg border border-gray-200 animate-pulse" />}
      {!weeksQuery.isLoading && weeks.length === 0 && <p className="text-sm text-gray-500">{t('cycleReports.nutrition.noWeeks')}</p>}
      {weeks.map((w) => (
        <CycleWeekPanel key={w.plan_id ?? `gap-${w.week_start}`} cycleId={cycleId} week={w}
          defaultOpen={w.plan_id != null && (w.plan_id === openPlanId || w.plan_id === firstCurrent)} />
      ))}
    </div>
  )
}

export default CycleWeeks
